import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Terminal, Plus, X, Save, Trash2, Eye, EyeOff, Edit3, ExternalLink, BookOpen,
} from "lucide-react";

// Phase 25.14b — Admin: DARA Pulse Wall curation.
// Public version lives at /wall.

const EMPTY_PULSE = {
  operator_name: "",
  industry: "",
  city: "",
  pulse_text: "",
  money_saved_label: "",
  case_study_slug: "",
  published: false,
  sort_order: 0,
};

export default function AdminPulseWall() {
  const [items, setItems] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [w, cs] = await Promise.all([
        api.get("/admin/wall/pulses"),
        api.get("/admin/case-studies"),
      ]);
      setItems(w.data?.items || []);
      setCaseStudies(cs.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY_PULSE });
  const startEdit = (row) => setEditing({ ...row });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing.operator_name?.trim() || !editing.pulse_text?.trim()) {
      toast.error("Operator name + pulse text are required");
      return;
    }
    if ((editing.pulse_text || "").length > 240) {
      toast.error("Pulse text must be ≤ 240 characters");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        operator_name: editing.operator_name.trim(),
        industry: (editing.industry || "").trim(),
        city: (editing.city || "").trim(),
        pulse_text: editing.pulse_text.trim(),
        money_saved_label: (editing.money_saved_label || "").trim(),
        case_study_slug: (editing.case_study_slug || "").trim(),
        published: Boolean(editing.published),
        sort_order: Number(editing.sort_order) || 0,
      };
      if (editing.id) {
        await api.patch(`/admin/wall/pulses/${editing.id}`, payload);
        toast.success("Pulse updated");
      } else {
        await api.post("/admin/wall/pulses", payload);
        toast.success("Pulse created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (row) => {
    setBusy(true);
    try {
      await api.patch(`/admin/wall/pulses/${row.id}`, {
        operator_name: row.operator_name,
        industry: row.industry || "",
        city: row.city || "",
        pulse_text: row.pulse_text,
        money_saved_label: row.money_saved_label || "",
        case_study_slug: row.case_study_slug || "",
        published: !row.published,
        sort_order: row.sort_order || 0,
      });
      toast.success(!row.published ? "Pulse published to /wall" : "Pulse unpublished");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!confirm(`Delete pulse for "${row.operator_name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/wall/pulses/${row.id}`);
      toast.success("Pulse deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const counts = useMemo(() => ({
    total: items.length,
    published: items.filter(i => i.published).length,
    drafts: items.filter(i => !i.published).length,
  }), [items]);

  return (
    <div className="p-4 sm:p-6 space-y-5" data-testid="admin-pulse-wall">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            // ADMIN · PULSE WALL CURATION
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mt-1">
            DARA Pulse Wall
          </h1>
          <p className="text-xs text-zinc-500 max-w-2xl mt-2 leading-relaxed">
            Short, social-proof cards posted publicly to <code className="text-zinc-300">/wall</code>.
            One operator · one signal DARA caught · one number. Keep them tweet-length (≤ 240 chars).
            Link to a long-form case study when one exists.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/wall"
            target="_blank"
            data-testid="open-public-wall"
            className="border border-zinc-800 hover:border-zinc-700 text-zinc-300 font-mono text-[11px] uppercase tracking-widest px-3 py-2 flex items-center gap-2"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View Public Wall
          </Link>
          <button
            onClick={startNew}
            data-testid="new-pulse-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 flex items-center gap-2 hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" /> New Pulse
          </button>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
        <StatCell label="TOTAL" value={counts.total} />
        <StatCell label="PUBLISHED" value={counts.published} accent="#00E676" />
        <StatCell label="DRAFTS" value={counts.drafts} accent="#FFB000" />
      </div>

      {loading && (
        <div className="text-center font-mono text-[11px] uppercase tracking-widest text-zinc-600 py-12">
          LOADING…
        </div>
      )}

      {!loading && items.length === 0 && !editing && (
        <div className="border border-zinc-900 bg-zinc-950/40 p-10 text-center">
          <Terminal className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <h3 className="font-heading font-bold text-lg">No pulses yet.</h3>
          <p className="text-sm text-zinc-400 mt-2 max-w-md mx-auto">
            Create your first pulse from a real operator&apos;s DARA briefing.
            One sentence, one shop, one signal.
          </p>
          <button
            onClick={startNew}
            className="mt-4 inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2.5"
          >
            <Plus className="w-3.5 h-3.5" /> Create Pulse
          </button>
        </div>
      )}

      {/* Editor */}
      {editing && (
        <div className="border border-[#FFB000]/40 bg-[#FFB000]/[0.04] p-5" data-testid="pulse-editor">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg">
              {editing.id ? "Edit Pulse" : "New Pulse"}
            </h2>
            <button onClick={cancel} className="text-zinc-500 hover:text-white p-1" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Operator name *">
              <input
                data-testid="pulse-operator-input"
                value={editing.operator_name}
                onChange={e => setEditing({ ...editing, operator_name: e.target.value })}
                placeholder="Sharp & Co."
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              />
            </Field>
            <Field label="Industry">
              <input
                data-testid="pulse-industry-input"
                value={editing.industry}
                onChange={e => setEditing({ ...editing, industry: e.target.value })}
                placeholder="Barber Shop"
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              />
            </Field>
            <Field label="City">
              <input
                data-testid="pulse-city-input"
                value={editing.city}
                onChange={e => setEditing({ ...editing, city: e.target.value })}
                placeholder="Sydney"
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              />
            </Field>
          </div>

          <div className="mt-4">
            <Field
              label={`Pulse text * (${(editing.pulse_text || "").length}/240)`}
            >
              <textarea
                data-testid="pulse-text-input"
                value={editing.pulse_text}
                onChange={e => setEditing({ ...editing, pulse_text: e.target.value.slice(0, 240) })}
                rows={3}
                placeholder="DARA caught 3 voids in a single morning — apprentice was punching wrong totals. Saved ~$120/day."
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000] leading-relaxed"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
            <Field label="Money saved label (optional)">
              <input
                data-testid="pulse-money-input"
                value={editing.money_saved_label}
                onChange={e => setEditing({ ...editing, money_saved_label: e.target.value })}
                placeholder="~$120/day"
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              />
            </Field>
            <Field label="Linked case study (optional)">
              <select
                data-testid="pulse-case-study-select"
                value={editing.case_study_slug}
                onChange={e => setEditing({ ...editing, case_study_slug: e.target.value })}
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              >
                <option value="">— None —</option>
                {caseStudies.map(cs => (
                  <option key={cs.slug} value={cs.slug}>
                    {cs.customer_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Sort order (higher = first)">
              <input
                data-testid="pulse-sort-input"
                type="number"
                value={editing.sort_order}
                onChange={e => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })}
                className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]"
              />
            </Field>
          </div>

          <label className="flex items-center gap-2 mt-4 font-mono text-[11px] uppercase tracking-widest text-zinc-400 cursor-pointer w-fit">
            <input
              type="checkbox"
              data-testid="pulse-published-checkbox"
              checked={Boolean(editing.published)}
              onChange={e => setEditing({ ...editing, published: e.target.checked })}
              className="accent-[#FFB000]"
            />
            Publish to public /wall
          </label>

          <div className="flex items-center gap-2 mt-5 pt-4 border-t border-zinc-900">
            <button
              onClick={save}
              disabled={busy}
              data-testid="pulse-save-btn"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2.5 flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save Pulse"}
            </button>
            <button
              onClick={cancel}
              className="border border-zinc-800 hover:border-zinc-700 text-zinc-300 font-mono text-[11px] uppercase tracking-widest px-4 py-2.5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {!loading && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map(p => (
            <div
              key={p.id}
              data-testid={`pulse-row-${p.id}`}
              className={`border p-4 flex flex-col gap-3 ${
                p.published ? "border-[#00E676]/30 bg-[#00E676]/[0.03]" : "border-zinc-900 bg-zinc-950/40"
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm text-white truncate">
                    {p.operator_name}
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
                    {[p.industry, p.city].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {p.case_study_slug && (
                    <span title="Linked to case study" className="text-zinc-500">
                      <BookOpen className="w-3.5 h-3.5" />
                    </span>
                  )}
                  <span
                    className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 border ${
                      p.published
                        ? "text-[#00E676] border-[#00E676]/40"
                        : "text-zinc-500 border-zinc-800"
                    }`}
                  >
                    {p.published ? "LIVE" : "DRAFT"}
                  </span>
                </div>
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed">&ldquo;{p.pulse_text}&rdquo;</p>
              {p.money_saved_label && (
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">
                  {p.money_saved_label}
                </div>
              )}
              <div className="flex items-center gap-2 pt-2 border-t border-zinc-900">
                <button
                  onClick={() => startEdit(p)}
                  data-testid={`pulse-edit-${p.id}`}
                  className="text-zinc-400 hover:text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1"
                >
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
                <button
                  onClick={() => togglePublish(p)}
                  data-testid={`pulse-toggle-${p.id}`}
                  className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1 ${
                    p.published ? "text-zinc-500 hover:text-white" : "text-[#00E676] hover:text-white"
                  }`}
                >
                  {p.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {p.published ? "Unpublish" : "Publish"}
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`pulse-delete-${p.id}`}
                  className="text-zinc-500 hover:text-[#FF6B47] font-mono text-[10px] uppercase tracking-widest px-2 py-1 flex items-center gap-1 ml-auto"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function StatCell({ label, value, accent = "#FFFFFF" }) {
  return (
    <div className="bg-black px-4 py-3">
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600">{label}</div>
      <div className="font-heading font-black text-2xl tracking-tighter mt-1" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}
